import type {
  ApiError,
  AuthUser,
  BulkImportRequest,
  BulkImportResult,
  ChangePasswordRequest,
  CreateProfileLinkRequest,
  CreateUserRequest,
  GatewayStatusDto,
  LoginRequest,
  ProfileLink,
  StatsResponse,
  UpdateUserRequest,
  User,
} from "@/types";

const API_BASE = "";

let csrfToken: string | null = null;

export function clearCsrfToken() {
  csrfToken = null;
}

async function fetchCsrfToken(): Promise<string> {
  if (csrfToken) return csrfToken;
  const res = await fetch(`${API_BASE}/api/antiforgery/token`, {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!res.ok) {
    throw new Error("Failed to fetch CSRF token");
  }
  const data = (await res.json()) as { token?: string };
  if (!data.token) {
    throw new Error("CSRF token missing");
  }
  csrfToken = data.token;
  return csrfToken;
}

async function apiFetch<T>(
  input: RequestInfo,
  init: RequestInit = {},
  options: { mutating?: boolean } = {}
): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  if (init.body && !(init.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }

  if (options.mutating) {
    const token = await fetchCsrfToken();
    headers.set("X-XSRF-TOKEN", token);
  }

  const res = await fetch(`${API_BASE}${input}`, {
    ...init,
    credentials: "include",
    headers,
  });

  if (res.status === 401) {
    window.location.href = "/login";
    throw new Error("Unauthorized");
  }

  if (!res.ok) {
    let message = `Request failed: ${res.status}`;
    try {
      const err = (await res.json()) as ApiError;
      if (err.error) message = err.error;
    } catch {
      // ignore
    }
    throw new Error(message);
  }

  if (res.status === 204) {
    return undefined as T;
  }

  return (await res.json()) as T;
}

export const api = {
  auth: {
    login: (data: LoginRequest) =>
      apiFetch<AuthUser>("/api/auth/login", { method: "POST", body: JSON.stringify(data) }, { mutating: true }),
    logout: () => apiFetch<void>("/api/auth/logout", { method: "POST" }, { mutating: true }),
    me: async () => {
      const res = await fetch(`${API_BASE}/api/auth/me`, {
        method: "GET",
        credentials: "include",
        headers: { Accept: "application/json" },
      });
      if (res.status === 401) return null;
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return (await res.json()) as AuthUser;
    },
  },
  users: {
    list: () => apiFetch<User[]>("/api/users"),
    create: (data: CreateUserRequest) =>
      apiFetch<User>("/api/users", { method: "POST", body: JSON.stringify(data) }, { mutating: true }),
    update: (uid: string, data: UpdateUserRequest) =>
      apiFetch<User>(`/api/users/${encodeURIComponent(uid)}`, { method: "PUT", body: JSON.stringify(data) }, { mutating: true }),
    delete: (uid: string) =>
      apiFetch<void>(`/api/users/${encodeURIComponent(uid)}`, { method: "DELETE" }, { mutating: true }),
    changePassword: (uid: string, data: ChangePasswordRequest) =>
      apiFetch<void>(
        `/api/users/${encodeURIComponent(uid)}/password`,
        { method: "POST", body: JSON.stringify(data) },
        { mutating: true }
      ),
    bulkImport: (data: BulkImportRequest) =>
      apiFetch<BulkImportResult>("/api/users/bulkimport", { method: "POST", body: JSON.stringify(data) }, { mutating: true }),
  },
  stats: {
    get: () => apiFetch<StatsResponse>("/api/stats"),
  },
  profileLinks: {
    list: () => apiFetch<ProfileLink[]>("/api/profile-links"),
    create: (data: CreateProfileLinkRequest) =>
      apiFetch<ProfileLink>("/api/profile-links", { method: "POST", body: JSON.stringify(data) }, { mutating: true }),
    revoke: (id: number) =>
      apiFetch<void>(`/api/profile-links/${id}`, { method: "DELETE" }, { mutating: true }),
  },
  status: {
    get: () => apiFetch<GatewayStatusDto>("/api/status"),
  },
};

export function formatBytes(bytes: number): string {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

export function formatDateTime(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatDuration(iso?: string | null): string {
  if (!iso) return "—";
  const match = /^(\d+):(\d{2}):(\d{2})/.exec(iso);
  if (!match) return iso;
  const [, h, m, s] = match;
  return `${h}h ${m}m ${s}s`;
}
